import {
	Action,
	Cancel,
	Content,
	Description,
	Overlay,
	Portal,
	Root,
	Title,
	Trigger,
} from '@radix-ui/react-alert-dialog';
import classNames from 'classnames';

import { Button } from 'components';

import type { ArrayOf } from 'types';
import type { AlertDialogProps as RadixAlertDialogProps } from '@radix-ui/react-alert-dialog';

interface AlertResponseOption {
	/** The value to assign to this alert response. */
	value: string;

	/** The label to use for the button that returns the associated response value. */
	label: string;

	/** Supporting text shown beneath the label in the stacked response layout. */
	description?: string;
}

export interface AlertProps extends RadixAlertDialogProps {
	/** The title text to display at the top of this alert. */
	title?: string;

	type?: 'Error' | 'Warning';

	body?: JSX.Element;

	/** The content of this alert, outlining the decision the user needs to make before proceeding. */
	description?: string;

	/** The response options to present to the user in this alert. */
	responseOptions: ArrayOf<'at least', 1, AlertResponseOption>;

	/** How response options should be arranged. */
	responseLayout?: 'inline' | 'stacked';

	/** The settings for the "do nothing" (or otherwise least-destructive) response option in this alert. */
	doNothingResponse?: AlertResponseOption;

	/** The handler function to invoke when the user selects a response from this alert. */
	onResponse: (value: string) => void;

	/** If header section should be included */
	includeHeader?: boolean;
}

/**
 * Applies a modal dialog to an element that interrupts the user with important
 * information that requires a response.
 */
export function Alert({
	title = 'Alert',
	description,
	type,
	body,
	includeHeader = true,
	responseOptions,
	responseLayout = 'inline',
	doNothingResponse = { value: 'do_nothing', label: 'Cancel' },
	onResponse: responseHandler = (value: string) => value,
	children,
	...props
}: AlertProps) {
	return (
		<Root {...props}>
			{children && <Trigger asChild>{children}</Trigger>}

			<Portal>
				<Overlay
					className={classNames(
						'fixed inset-0 bg-black/40 z-alert',
						'data-open:animate-displayOverlay data-closed:animate-dismissOverlay'
					)}
				/>

				<div className='fixed inset-0 grid place-items-center z-alert'>
					<Content
						className={classNames(
							'max-w-2xl bg-white border border-blue-500 rounded-sm',
							responseLayout === 'stacked' && 'w-[calc(100vw-2rem)]',
							'data-open:animate-displayAlert data-closed:animate-dismissAlert'
						)}
						onEscapeKeyDown={() => responseHandler(doNothingResponse.value)}
					>
						{includeHeader && (
							<Title className='flex items-center gap-3 py-4 px-6 bg-blue-50 border-b border-inherit'>
								{type === 'Error' ? (
									<span className='material-symbol-[2rem/fill] text-red-700'>error</span>
								) : (
									<span className='material-symbol-[2rem/fill] text-yellow-700'>warning</span>
								)}
								<span className='text-h5 font-semibold'>{title}</span>
							</Title>
						)}

						<div className='p-6'>
							{description && (
								<Description className={classNames('py-2 leading-relaxed', responseLayout === 'stacked' && 'text-p1')}>
									{description}
								</Description>
							)}

							{body}

							{responseLayout === 'stacked' ? (
								<div className='flex flex-col gap-2 mt-5'>
									{responseOptions.map(({ value, label, description: optionDescription }) => (
										<Action key={value} onClick={() => responseHandler(value)} asChild>
											<Button
												variant='transparent'
												sizeX='full'
												sizeY='md'
												squareCorners={false}
												className='!h-auto min-h-[3.75rem] !justify-start border border-blue-300 px-4 py-3 text-left text-blue-700 enabled:hover:border-blue-500 enabled:hover:bg-blue-50'
											>
												<span className='flex w-full flex-col items-start gap-1'>
													<span className='text-p1 font-semibold leading-tight'>{label}</span>
													{optionDescription && (
														<span className='text-p2 font-normal leading-snug text-bluegray-700'>{optionDescription}</span>
													)}
												</span>
											</Button>
										</Action>
									))}
									<Cancel onClick={() => responseHandler(doNothingResponse.value)} asChild>
										<Button
											variant='transparent'
											sizeX='full'
											sizeY='sm'
											className='!text-p2 text-bluegray-700 enabled:hover:bg-bluegray-50'
										>
											{doNothingResponse.label}
										</Button>
									</Cancel>
								</div>
							) : (
								<div className='flex justify-end gap-2 mt-6'>
									{responseOptions.map(({ value, label }) => (
										<Action key={value} onClick={() => responseHandler(value)} asChild>
											<Button sizeX='sm' sizeY='sm'>
												{label}
											</Button>
										</Action>
									))}
									<Cancel onClick={() => responseHandler(doNothingResponse.value)} asChild>
										<Button variant='secondary' sizeX='sm' sizeY='sm'>
											{doNothingResponse.label}
										</Button>
									</Cancel>
								</div>
							)}
						</div>
					</Content>
				</div>
			</Portal>
		</Root>
	);
}

export default Alert;
